import type { ActivityEvent, ContractSchedule, OptionPeriodView, PreAwardMilestone, Suspense } from '../api/trackerFunctions';

export interface TimelineExtent { start: string; end: string; }
export interface PlannedBar { milestone: PreAwardMilestone; start: string; end: string; }
export interface PlannedTarget { milestone: PreAwardMilestone; date: string; }
export interface ActualMilestone { milestone: PreAwardMilestone; event: ActivityEvent; date: string; }
export interface ActivityMarker { event: ActivityEvent; date: string; stackIndex: number; }
export interface SuspenseMarker { suspense: Suspense; optionPeriodId: string; dueDate: string; }
export interface ContractScheduleGeometry {
  extent: TimelineExtent | null;
  preAwardBars: PlannedBar[];
  preAwardTargets: PlannedTarget[];
  unscheduledMilestones: PreAwardMilestone[];
  actualMilestones: ActualMilestone[];
  periods: OptionPeriodView[];
  suspenseMarkers: SuspenseMarker[];
  activityMarkers: ActivityMarker[];
}

const DAY = 86_400_000;
const isoDate = /^\d{4}-\d{2}-\d{2}$/;

/** Clamp the user-controlled chart detail level; 1x is always the full-date overview. */
export const timelineZoomMultiplier = (value: number): number => Math.min(4, Math.max(1, Math.round(value) || 1));

/** Parse a date-only string in UTC, rejecting JavaScript's rollover parsing. */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value || !isoDate.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
    ? parsed : null;
}

export function parseTimestampDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp) : null;
}

const calendarDateFromTimestamp = (value: string): string | null => {
  const parsed = parseTimestampDate(value);
  return parsed ? parsed.toISOString().slice(0, 10) : null;
};

const validRange = (start: string | null, end: string | null): boolean => {
  const startDate = parseCalendarDate(start);
  const endDate = parseCalendarDate(end);
  return Boolean(startDate && endDate && endDate.getTime() > startDate.getTime());
};

const chronological = <T>(items: T[], date: (item: T) => string, id: (item: T) => string): T[] => [...items]
  .sort((a, b) => date(a).localeCompare(date(b)) || id(a).localeCompare(id(b)));

/** Build all chart-ready rows without backfilling dates from audit metadata. */
export function buildContractScheduleGeometry(schedule: ContractSchedule): ContractScheduleGeometry {
  const milestones = [...schedule.preAwardMilestones].sort((a, b) => a.workflowOrder - b.workflowOrder || a.id.localeCompare(b.id));
  const preAwardBars = milestones.flatMap((milestone) => validRange(milestone.plannedStartDate, milestone.plannedEndDate)
    ? [{ milestone, start: milestone.plannedStartDate!, end: milestone.plannedEndDate! }] : []);
  const preAwardTargets = milestones.flatMap((milestone) => !milestone.plannedStartDate && !milestone.plannedEndDate && parseCalendarDate(milestone.targetDate)
    ? [{ milestone, date: milestone.targetDate! }] : []);
  const scheduledIds = new Set([...preAwardBars.map(row => row.milestone.id), ...preAwardTargets.map(row => row.milestone.id)]);
  const unscheduledMilestones = milestones.filter(milestone => !scheduledIds.has(milestone.id));
  const periods = schedule.optionPeriods.filter(period => validRange(period.start, period.end));
  const periodIds = new Set(periods.map(period => period.optionPeriodId));
  const suspenseMarkers = chronological(
    schedule.suspenses.flatMap(suspense => parseCalendarDate(suspense.dueDate) && periodIds.has(suspense.optionPeriodId)
      ? [{ suspense, optionPeriodId: suspense.optionPeriodId, dueDate: suspense.dueDate }] : []),
    item => item.dueDate,
    item => item.suspense.eventKey,
  );
  const activityCandidates = schedule.activityEvents.flatMap(event => {
      const date = event.occurredAt ? calendarDateFromTimestamp(event.occurredAt) : null;
      const occurredAt = event.occurredAt ? parseTimestampDate(event.occurredAt)?.getTime() : undefined;
      return date && occurredAt !== undefined ? [{ event, date, occurredAt }] : [];
    });
  const activityStackCounts = new Map<string, number>();
  const activityMarkers = activityCandidates
    .sort((a, b) => a.occurredAt - b.occurredAt || a.event.eventId.localeCompare(b.event.eventId))
    .map(({ event, date }) => {
      const stackIndex = activityStackCounts.get(date) ?? 0;
      activityStackCounts.set(date, stackIndex + 1);
      return { event, date, stackIndex };
    });
  const eventsById = new Map(activityMarkers.map(marker => [marker.event.eventId, marker]));
  const actualMilestones = milestones.flatMap(milestone => {
    const marker = milestone.actualSourceActionId ? eventsById.get(milestone.actualSourceActionId) : undefined;
    const actualDate = milestone.actualOccurredAt ? calendarDateFromTimestamp(milestone.actualOccurredAt) : null;
    return marker && actualDate ? [{ milestone, event: marker.event, date: actualDate }] : [];
  });
  const dates = [
    ...preAwardBars.flatMap(row => [row.start, row.end]), ...preAwardTargets.map(row => row.date),
    ...actualMilestones.map(row => row.date), ...periods.flatMap(period => [period.start, period.end]),
    ...suspenseMarkers.map(marker => marker.dueDate), ...activityMarkers.map(marker => marker.date),
  ].filter((value): value is string => Boolean(parseCalendarDate(value)));
  const orderedDates = [...dates].sort();
  return {
    extent: orderedDates.length ? { start: orderedDates[0], end: orderedDates[orderedDates.length - 1] } : null,
    preAwardBars, preAwardTargets, unscheduledMilestones, actualMilestones, periods, suspenseMarkers, activityMarkers,
  };
}

export function datePosition(value: string, extent: TimelineExtent): number {
  const point = parseCalendarDate(value)?.getTime();
  const start = parseCalendarDate(extent.start)?.getTime();
  const end = parseCalendarDate(extent.end)?.getTime();
  if (point === undefined || start === undefined || end === undefined || !Number.isFinite(point) || end <= start) return 0;
  return Math.max(0, Math.min(100, ((point - start) / (end - start)) * 100));
}

export function todayPlacement(today: string, extent: TimelineExtent): { state: 'in_range' | 'before' | 'after'; position: number | null } {
  const date = parseCalendarDate(today)?.getTime();
  const start = parseCalendarDate(extent.start)?.getTime();
  const end = parseCalendarDate(extent.end)?.getTime();
  if (!Number.isFinite(date) || !Number.isFinite(start) || !Number.isFinite(end)) return { state: 'before', position: null };
  if (date! < start!) return { state: 'before', position: null };
  if (date! > end!) return { state: 'after', position: null };
  return { state: 'in_range', position: datePosition(today, extent) };
}

export const inclusiveWidth = (start: string, end: string, extent: TimelineExtent): number => {
  const startPosition = datePosition(start, extent);
  const endPosition = datePosition(end, extent);
  return Math.max(0.5, endPosition - startPosition + (DAY / Math.max(DAY, (parseCalendarDate(extent.end)!.getTime() - parseCalendarDate(extent.start)!.getTime() + DAY))) * 100);
};
